import { styled } from "goober";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useScrollFade } from "../hooks/useScrollFade";
import { GhostPill } from "./GhostPill";
import { Link } from "./Link";
import { Text } from "./Text";
import type { StatusSegment } from "./StatusBreakdownChart";

const BASE_SIZE = 240;

const GHOST_SEGMENTS: StatusSegment[] = [
  { key: "ghost-1", count: 5, color: "var(--alpha8)", label: "" },
  { key: "ghost-2", count: 3, color: "var(--alpha8)", label: "" },
  { key: "ghost-3", count: 2, color: "var(--alpha8)", label: "" },
  { key: "ghost-4", count: 1, color: "var(--alpha8)", label: "" },
];
const GHOST_TOTAL = GHOST_SEGMENTS.reduce((sum, s) => sum + s.count, 0);

const Outer = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-height: 0;
`;

const RingArea = styled("div")`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Ring = styled("div")`
  position: relative;
  flex-shrink: 0;
`;

const Arc = styled("circle")`
  fill: none;
  pointer-events: none;
  transition:
    opacity 120ms ease,
    stroke-width 120ms ease;
`;

// Invisible full-slice wedge so hovering anywhere in a segment's pie sector
// (not just the thin arc) drives the hover state.
const Wedge = styled("path")`
  fill: transparent;
`;

const Center = styled("div")`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  text-align: center;
  pointer-events: none;
`;

const Legend = styled("div")`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 12px;

  &[data-scrollable="true"] {
    max-height: 76px;
    overflow-y: auto;
    -webkit-mask-image: linear-gradient(
      to bottom,
      transparent,
      black var(--fade-top, 0px),
      black calc(100% - var(--fade-bottom, 0px)),
      transparent
    );
    mask-image: linear-gradient(
      to bottom,
      transparent,
      black var(--fade-top, 0px),
      black calc(100% - var(--fade-bottom, 0px)),
      transparent
    );
  }
`;

const LegendItem = styled("div")`
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  cursor: default;
  transition: opacity 120ms ease;
`;

const LegendDot = styled("span")`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
`;

type Props = {
  segments: ReadonlyArray<StatusSegment>;
  total: number;
  // Denominator for the capacity arcs; defaults to `total`.
  capacityTotal?: number;
  formatValue?: (value: number) => string;
  centerLabel?: string;
  centerCaption?: string;
  segmentHref?: (segment: StatusSegment) => string | undefined;
  strokeWidth?: number;
  placeholder?: boolean;
  scrollableLegend?: boolean;
};

export function DonutChart({
  segments,
  total,
  capacityTotal,
  formatValue = (n) => String(n),
  centerLabel,
  centerCaption,
  segmentHref,
  strokeWidth = 14,
  placeholder,
  scrollableLegend,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  useScrollFade({ scrollRef: legendRef });
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const hoverSource = useRef<"arc" | "legend" | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () =>
      setBox({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Hovering an arc reveals its row in the scrollable legend, so the highlight
  // isn't stranded off-screen. Only for arc hovers — scrolling while the cursor
  // is on a legend row would slide it out from under the pointer. Scoped to the
  // legend (scrollBy on the container) so the page never moves; the margin keeps
  // the row clear of the top/bottom fade.
  useEffect(() => {
    if (
      !scrollableLegend ||
      hoveredKey == null ||
      hoverSource.current !== "arc"
    )
      return;
    const container = legendRef.current;
    const item = container?.querySelector<HTMLElement>(
      `[data-legend-key="${CSS.escape(hoveredKey)}"]`
    );
    if (!container || !item) return;
    const c = container.getBoundingClientRect();
    const i = item.getBoundingClientRect();
    const margin = 18;
    if (i.top < c.top + margin) {
      container.scrollBy({ top: i.top - c.top - margin, behavior: "smooth" });
    } else if (i.bottom > c.bottom - margin) {
      container.scrollBy({
        top: i.bottom - c.bottom + margin,
        behavior: "smooth",
      });
    }
  }, [hoveredKey, scrollableLegend]);

  const shown = placeholder
    ? GHOST_SEGMENTS
    : segments.filter((s) => (s.capacity ?? s.count) > 0);
  const legendSegments = shown.filter((s) => !s.hideFromLegend);
  // In capacity mode zero spend still draws the track arcs, so emptiness is
  // judged against the arc denominator, not the spend total.
  const denom = placeholder ? GHOST_TOTAL : (capacityTotal ?? total);
  const isEmpty = !placeholder && (denom <= 0 || shown.length === 0);

  const hovered =
    hoveredKey != null
      ? (shown.find((s) => s.key === hoveredKey) ?? null)
      : null;
  const centerTop =
    centerLabel != null ? (hovered ? hovered.label : centerLabel) : null;
  const centerBottom =
    centerLabel != null
      ? hovered
        ? hovered.caption
        : centerCaption
      : hovered
        ? hovered.label
        : centerCaption;

  // Size the donut to fill the space the panel leaves for it. The stroke keeps
  // its pixel thickness at any diameter, so the ring stays a delicate, constant
  // weight rather than thickening as it grows; BASE_SIZE is the fallback before
  // the first measurement.
  const size =
    box.width > 0 && box.height > 0
      ? Math.min(box.width, box.height)
      : BASE_SIZE;
  // Reserve room for the thickest an arc can get when hover-expanded.
  const maxStroke = strokeWidth * 2;
  const radius = Math.max((size - maxStroke) / 2, 1);
  const circumference = 2 * Math.PI * radius;

  // Full pie-sector hit areas so hovering anywhere in a segment's wedge — not
  // just the thin arc — drives the hover state. Angles match the arcs: both
  // start at 3 o'clock and the shared rotate(-90) turns that to the top.
  const c = size / 2;
  let cumFraction = 0;
  const wedges =
    isEmpty || placeholder
      ? []
      : shown.map((segment) => {
          const fraction =
            denom > 0 ? (segment.capacity ?? segment.count) / denom : 0;
          const a0 = cumFraction * 2 * Math.PI;
          const a1 = (cumFraction + Math.min(fraction, 0.9999)) * 2 * Math.PI;
          cumFraction += fraction;
          const large = fraction > 0.5 ? 1 : 0;
          const x0 = c + c * Math.cos(a0);
          const y0 = c + c * Math.sin(a0);
          const x1 = c + c * Math.cos(a1);
          const y1 = c + c * Math.sin(a1);
          return {
            segment,
            d: `M${c},${c}L${x0},${y0}A${c},${c} 0 ${large} 1 ${x1},${y1}Z`,
          };
        });

  let offset = 0;
  return (
    <Outer aria-hidden={placeholder || undefined}>
      <RingArea ref={ref}>
        <Ring style={{ width: size, height: size }}>
          <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
            <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
              {isEmpty && (
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="var(--alpha4)"
                  strokeWidth={strokeWidth}
                />
              )}
              {!isEmpty &&
                shown.map((segment) => {
                  const cap = segment.capacity ?? segment.count;
                  const fraction = denom > 0 ? cap / denom : 0;
                  const spentFraction =
                    segment.capacity == null
                      ? 1
                      : cap > 0
                        ? Math.min(segment.count / cap, 1)
                        : 0;
                  // Round caps overhang each arc end by stroke/2, so the dash gap
                  // adds both adjacent caps to leave a ~2px visible gap.
                  const gap = 2 + strokeWidth;
                  const fullArc = Math.max(fraction * circumference - gap, 0.5);
                  const solidArc = fullArc * spentFraction;
                  const dashOffset = -offset;
                  offset += fraction * circumference;
                  const active = hoveredKey === segment.key;
                  const dimmed = hoveredKey != null && !active;
                  const arcStroke = active ? strokeWidth * 2 : strokeWidth;
                  const trackOpacity = segment.trackColor
                    ? dimmed
                      ? 0.3
                      : 1
                    : dimmed
                      ? 0.16
                      : 0.4;
                  return (
                    <g key={segment.key}>
                      {spentFraction < 1 && (
                        <Arc
                          cx={size / 2}
                          cy={size / 2}
                          r={radius}
                          stroke={segment.trackColor ?? segment.color}
                          strokeLinecap="round"
                          strokeDasharray={`${fullArc} ${circumference - fullArc}`}
                          strokeDashoffset={dashOffset}
                          style={{
                            strokeWidth: arcStroke,
                            opacity: trackOpacity,
                          }}
                        />
                      )}
                      {spentFraction > 0 && (
                        <Arc
                          cx={size / 2}
                          cy={size / 2}
                          r={radius}
                          stroke={segment.color}
                          strokeLinecap="round"
                          strokeDasharray={`${solidArc} ${circumference - solidArc}`}
                          strokeDashoffset={dashOffset}
                          style={{
                            strokeWidth: arcStroke,
                            opacity: dimmed ? 0.3 : 1,
                          }}
                        />
                      )}
                    </g>
                  );
                })}
              {wedges.map(({ segment, d }) => {
                const wedge = (
                  <Wedge
                    d={d}
                    onMouseEnter={() => {
                      hoverSource.current = "arc";
                      setHoveredKey(segment.key);
                    }}
                    onMouseLeave={() => setHoveredKey(null)}
                  >
                    <title>
                      {segment.label}: {formatValue(segment.count)}
                    </title>
                  </Wedge>
                );
                const href = segmentHref?.(segment);
                return href ? (
                  <Link
                    key={`hit-${segment.key}`}
                    to={href}
                    aria-label={segment.label}
                    style={{ cursor: "pointer" }}
                  >
                    {wedge}
                  </Link>
                ) : (
                  <g key={`hit-${segment.key}`}>{wedge}</g>
                );
              })}
            </g>
          </svg>
          <Center>
            {centerTop ? (
              <Text variant="text-small" color="var(--text-secondary)">
                {centerTop}
              </Text>
            ) : null}
            <Text variant="display-md" color="var(--text-primary)">
              {hovered
                ? formatValue(hovered.count)
                : placeholder
                  ? formatValue(total)
                  : isEmpty
                    ? "–"
                    : formatValue(total)}
            </Text>
            {centerBottom ? (
              <Text variant="text-small" color="var(--text-secondary)">
                {centerBottom}
              </Text>
            ) : null}
          </Center>
        </Ring>
      </RingArea>

      {legendSegments.length > 0 && (
        <Legend
          ref={legendRef}
          data-scrollable={scrollableLegend ? "true" : undefined}
        >
          {legendSegments.map((segment) =>
            placeholder ? (
              <LegendItem key={segment.key}>
                <LegendDot style={{ background: segment.color }} />
                <GhostPill style={{ width: 16 + segment.count * 8 }} />
              </LegendItem>
            ) : (
              <LegendItem
                key={segment.key}
                data-legend-key={segment.key}
                onMouseEnter={() => {
                  hoverSource.current = "legend";
                  setHoveredKey(segment.key);
                }}
                onMouseLeave={() => setHoveredKey(null)}
                style={{
                  opacity:
                    hoveredKey != null && hoveredKey !== segment.key ? 0.4 : 1,
                }}
              >
                <LegendDot style={{ background: segment.color }} />
                <Text variant="text-small" color="var(--text-secondary)">
                  {segment.label}
                </Text>
              </LegendItem>
            )
          )}
        </Legend>
      )}
    </Outer>
  );
}
